/**
 * CMS pagination loader — fetches the remaining pages of a Webflow
 * paginated Collection List and extracts their items.
 *
 * Webflow renders at most 100 items per Collection List, so any list longer
 * than that is only reachable through its `?<id>_page=N` links. This walks
 * those links, parses each page off-thread-ish via DOMParser, and hands the
 * item elements back so they can be adopted into the live list.
 *
 * Pages are prefetched in the background so a load feels instant. Prefetch
 * is opt-in (`autoPrefetch`) because the fetched documents are full pages —
 * cheap over the wire once compressed, but not free.
 *
 * @example
 * ```ts
 * const loader = createLoader({
 *   container: gridEl,
 *   itemSelector: '[int-filter="item"]',
 *   onLoad: (els) => { els.forEach(el => grid.appendChild(el)); list.addItems(els); },
 * });
 *
 * await loader.loadAll();
 * ```
 */

// -------------------------------------------------------
// Types
// -------------------------------------------------------

export interface LoaderConfig {
  /** Element containing the collection items (scope for the pagination link) */
  container: HTMLElement;
  /** CSS selector for individual items within a fetched page */
  itemSelector: string;
  /**
   * Selector locating the list container inside a *fetched* page. Without it
   * `itemSelector` runs against the whole document and picks up items from
   * every other Collection List on the page (nav promos, footers). Required
   * whenever `itemSelector` isn't unique to this list.
   */
  scopeSelector?: string;
  /** Selector for the "next page" link. Defaults to Webflow's own. */
  nextSelector?: string;
  /** Hide Webflow's native pagination UI on init. Default true. */
  hideNative?: boolean;
  /** Start prefetching page 2 immediately on init. Default false. */
  autoPrefetch?: boolean;
  /**
   * Also fetch the pages *before* the current one. Default false.
   *
   * Webflow's pagination only links forward, so landing on `?…_page=3`
   * leaves pages 1–2 unreachable. That's fine while browsing, and wrong the
   * moment a search has to cover the whole collection — which is exactly
   * the link people share (`/integrations/data-points?b4c211f8_page=2`).
   *
   * Earlier pages are fetched in parallel and handed to `onLoad` in
   * ascending page order, so a caller that orders by `meta.page` can put the
   * list back the way the CMS had it.
   */
  backfill?: boolean;
  /**
   * Name of Webflow's pagination query param (`b4c211f8_page`). Inferred
   * from the next/previous link when omitted — only needed if neither is
   * present.
   */
  pageParam?: string;
  /** Called with new elements after each successful fetch */
  onLoad?: (elements: HTMLElement[], meta: { page: number }) => void;
  /** Called once every page has been loaded */
  onComplete?: () => void;
  /** Called on fetch error */
  onError?: (error: Error) => void;
}

export interface LoaderInstance {
  /** Load the next page. Resolves instantly if already prefetched. */
  load(): Promise<HTMLElement[]>;
  /** Fetch every remaining page. */
  loadAll(): Promise<HTMLElement[]>;
  /** Warm the next page in the background without adopting it. */
  prefetch(): void;
  /** 1-based index of the server-rendered page this loader started on. */
  readonly currentPage: number;
  /** Webflow's pagination query param for this list, if it has one. */
  readonly pageParam: string | null;
  /** Whether more pages remain */
  readonly hasMore: boolean;
  /** Whether a fetch is in flight */
  readonly isLoading: boolean;
  /** Pages loaded so far, including the server-rendered first page */
  readonly pagesLoaded: number;
}

// -------------------------------------------------------
// Implementation
// -------------------------------------------------------

const DEBUG = false;
const log = (...args: unknown[]) =>
  DEBUG && console.log("%c[loader]", "color:#ff6840;font-weight:bold", ...args);

/** Cap on <link rel=prefetch> hints per page — enough to cover a viewport
 *  or two of logos without flooding <head> with 100 tags per page. */
const IMG_PREFETCH_LIMIT = 24;

export function createLoader(config: LoaderConfig): LoaderInstance {
  const {
    container,
    itemSelector,
    scopeSelector,
    nextSelector = ".w-pagination-next",
    hideNative = true,
    autoPrefetch = false,
    backfill = false,
    pageParam,
    onLoad,
    onComplete,
    onError,
  } = config;

  let nextUrl = getNextUrl();
  let isLoading = false;
  let pagesLoaded = 1;
  let completed = false;

  // Captured once: `history.replaceState` moves the live URL around as
  // filters change, and the page range must not move with it.
  const baseSearch = new URLSearchParams(location.search);
  const param = pageParam ?? findPageParam();
  const currentPage = Math.max(1, Number(param && baseSearch.get(param)) || 1);
  let backfillPending = backfill && currentPage > 1 && !!param;
  // Walks forward independently of `pagesLoaded`, which the backfill also
  // bumps — the two must not share a counter.
  let forwardPage = currentPage;

  // prefetch cache — the next page's elements, ready to adopt
  let prefetched: {
    elements: HTMLElement[];
    nextPageUrl: string | null;
  } | null = null;
  let prefetchInFlight = false;
  let prefetchPromise: Promise<void> | null = null;

  function getNextUrl(): string | null {
    const scope =
      container.closest("[data-module]") || container.parentElement || document;
    const nextLink = scope.querySelector<HTMLAnchorElement>(nextSelector);
    if (!nextLink) return null;

    const href = nextLink.getAttribute("href");
    if (!href) return null;

    // We drive pagination ourselves — hide Webflow's control so the user
    // never sees two competing affordances.
    if (hideNative) {
      const wrap = nextLink.closest<HTMLElement>(".w-pagination-wrapper");
      (wrap ?? nextLink).style.display = "none";
    }

    return href;
  }

  /** Webflow names the param after the Collection List's element id
   *  (`b4c211f8_page`), so it has to be read off a real pagination link. */
  function findPageParam(): string | null {
    const scope =
      container.closest("[data-module]") || container.parentElement || document;
    for (const sel of [nextSelector, ".w-pagination-previous"]) {
      const href = scope.querySelector<HTMLAnchorElement>(sel)?.getAttribute("href");
      if (!href) continue;
      const qs = href.slice(href.indexOf("?") + 1);
      for (const key of new URLSearchParams(qs).keys()) {
        if (key.endsWith("_page")) return key;
      }
    }
    return null;
  }

  function urlForPage(page: number): string {
    const params = new URLSearchParams(baseSearch);
    if (page <= 1) params.delete(param!);
    else params.set(param!, String(page));
    const qs = params.toString();
    return `${location.pathname}${qs ? `?${qs}` : ""}`;
  }

  /** Pages 1…current-1, fetched in parallel, emitted in page order. */
  async function loadEarlier(): Promise<HTMLElement[]> {
    backfillPending = false;

    const pages = Array.from({ length: currentPage - 1 }, (_, i) => i + 1);
    const results = await Promise.all(
      pages.map((page) =>
        fetchPage(urlForPage(page))
          .then((r) => ({ page, elements: r.elements }))
          .catch((err) => {
            onError?.(err instanceof Error ? err : new Error(String(err)));
            return { page, elements: [] as HTMLElement[] };
          }),
      ),
    );

    const all: HTMLElement[] = [];
    for (const { page, elements } of results) {
      if (!elements.length) continue;
      pagesLoaded++;
      onLoad?.(elements, { page });
      all.push(...elements);
    }
    return all;
  }

  async function fetchPage(url: string) {
    const t0 = performance.now();

    const response = await fetch(url, { credentials: "same-origin" });
    if (!response.ok) {
      throw new Error(`Loader: failed to fetch ${url} (${response.status})`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");

    const scope = scopeSelector
      ? doc.querySelector<HTMLElement>(scopeSelector)
      : doc;
    if (!scope) {
      throw new Error(`Loader: scope "${scopeSelector}" not found in ${url}`);
    }

    // adoptNode detaches from the inert parsed document so the elements can
    // be appended live without a second clone pass.
    const elements = Array.from(
      scope.querySelectorAll<HTMLElement>(itemSelector),
    ).map((el) => document.adoptNode(el));

    // Scoped the same way the items are. `doc.querySelector` would return the
    // document's *first* pagination link, which on a page carrying two
    // paginated lists — `/blog` has "Latest" and "More posts" — is the other
    // list's, and this one would then walk a page range that isn't its own.
    const nextScope: ParentNode =
      scopeSelector && scope instanceof Element
        ? (scope.closest("[data-module]") ?? scope.parentElement ?? doc)
        : doc;
    const nextPageUrl =
      nextScope
        .querySelector<HTMLAnchorElement>(nextSelector)
        ?.getAttribute("href") || null;

    log(`fetched ${url}`, {
      items: elements.length,
      ms: Math.round(performance.now() - t0),
      nextPage: nextPageUrl || "(last page)",
    });

    warmImages(elements);

    return { elements, nextPageUrl };
  }

  // Ask the browser to pull the logos while the cards are still offscreen,
  // so appending them doesn't flash empty boxes.
  function warmImages(elements: HTMLElement[]) {
    let n = 0;
    for (const el of elements) {
      if (n >= IMG_PREFETCH_LIMIT) return;
      const img = el.querySelector<HTMLImageElement>("img");
      if (!img) continue;
      const src =
        img.getAttribute("src") ||
        img.getAttribute("srcset")?.split(",")[0]?.trim().split(" ")[0];
      if (!src) continue;
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.as = "image";
      link.href = src;
      document.head.appendChild(link);
      n++;
    }
  }

  function prefetch() {
    if (!nextUrl || prefetched || prefetchInFlight) return;

    log(`prefetching ${nextUrl}...`);
    prefetchInFlight = true;

    prefetchPromise = fetchPage(nextUrl)
      .then((result) => {
        prefetched = result;
        log(`prefetch ready (${result.elements.length} items)`);
      })
      .catch(() => {
        log("prefetch failed — will retry on load()");
      })
      .finally(() => {
        prefetchInFlight = false;
        prefetchPromise = null;
      });
  }

  async function load(): Promise<HTMLElement[]> {
    if (isLoading) return [];

    // Earlier pages first — they belong at the top of the list, and a caller
    // that appends in arrival order gets the collection back in CMS order.
    if (backfillPending) {
      isLoading = true;
      try {
        return await loadEarlier();
      } finally {
        isLoading = false;
      }
    }

    if (!nextUrl) return [];

    isLoading = true;

    try {
      // join an in-flight prefetch rather than racing a second request
      if (!prefetched && prefetchPromise) await prefetchPromise;

      const result = prefetched ?? (await fetchPage(nextUrl));
      prefetched = null;

      const page = ++forwardPage;

      nextUrl = result.nextPageUrl;
      pagesLoaded++;
      isLoading = false;

      if (result.elements.length) onLoad?.(result.elements, { page });

      if (!nextUrl) {
        if (!completed) {
          completed = true;
          onComplete?.();
        }
      } else {
        prefetch(); // keep one page warm ahead of the user
      }

      return result.elements;
    } catch (err) {
      isLoading = false;
      onError?.(err instanceof Error ? err : new Error(String(err)));
      return [];
    }
  }

  async function loadAll(): Promise<HTMLElement[]> {
    const all: HTMLElement[] = [];

    while (nextUrl || backfillPending) {
      const pendingBefore = backfillPending;
      const batch = await load();
      // load() returns [] on error as well as on exhaustion; without this the
      // loop would spin forever on a failing page. A backfill pass that comes
      // back empty is not a reason to skip the forward walk.
      if (!batch.length && nextUrl && !pendingBefore) break;
      all.push(...batch);
    }

    return all;
  }

  if (autoPrefetch) prefetch();

  return {
    load,
    loadAll,
    prefetch,
    get hasMore() {
      return !!nextUrl || backfillPending;
    },
    get isLoading() {
      return isLoading;
    },
    get pagesLoaded() {
      return pagesLoaded;
    },
    get currentPage() {
      return currentPage;
    },
    get pageParam() {
      return param;
    },
  };
}
